import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap } from "lucide-react";

/**
 * "In 30 Seconds..." — AI-generated quick-hit bullets shown at the top of an
 * article, right after the hero image. Fetches on mount, caches on the
 * backend article record so repeat visits don't spend LLM credits.
 */
export default function QuickHitsBox({ articleId }) {
  const [bullets, setBullets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(false);
      try {
        const { data } = await api.post(`/news/${articleId}/summarize`);
        if (!alive) return;
        setBullets(Array.isArray(data?.quick_hits) ? data.quick_hits : []);
      } catch {
        if (!alive) return;
        setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [articleId]);

  if (error || (!loading && bullets.length === 0)) return null;

  return (
    <aside
      data-testid="quick-hits-box"
      data-reveal
      className="my-5 md:my-8 relative overflow-hidden border-2 border-titans-blue/25 bg-gradient-to-br from-white to-[#F1F5FB] p-4 md:p-6"
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-titans-blue via-white to-titans-red" />
      <div className="flex items-center gap-2 mb-2 md:mb-3">
        <Zap size={16} className="text-titans-red" />
        <div className="font-cond uppercase tracking-[0.28em] text-[11px] text-titans-red font-bold">
          In 30 Seconds…
        </div>
        <div className="ml-auto font-cond uppercase tracking-wider text-[9px] text-titans-muted">
          Quick Hits
        </div>
      </div>
      {loading ? (
        <div className="space-y-2" data-testid="quick-hits-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-3 bg-[rgba(15,23,42,0.08)] animate-pulse rounded" style={{ width: `${75 - i * 8}%` }} />
          ))}
        </div>
      ) : (
        <ul className="space-y-1.5 md:space-y-2.5" data-testid="quick-hits-list">
          {bullets.map((b, i) => (
            <li key={i} data-testid={`quick-hit-${i}`} data-reveal style={{ "--reveal-i": i }} className="flex gap-2.5 md:gap-3 items-start">
              <span className="shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full bg-titans-red" />
              <span className="font-body text-[14px] md:text-[16px] leading-[1.4] md:leading-snug text-[#0C1220]">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
